import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useRef, useState } from "react";
import './DragAndDropInput.scss';
import classNames from "classnames";
import { v4 } from "uuid";
import { Button, Col } from "react-bootstrap";
import { Flex } from "antd";
import IconButton from "../common/IconButton";

type DragAndDropInputProps = {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  onChange: (file: File[]) => void;
  onFolderChange?: (folder: string) => void;
}

declare module 'react' {
  interface InputHTMLAttributes<T> extends HTMLAttributes<T> {
    // extends React's HTMLAttributes
    directory?: string;
    webkitdirectory?: string;
  }
}

type FileInfo = { id: string, file: File };

export default function DragAndDropInput({ onChange, onFolderChange, title = "Drop your files here", subtitle = "Browse files" }: DragAndDropInputProps) {
  const fileBrowser = useRef<HTMLInputElement>(null);
  const directoryBrowser = useRef<HTMLInputElement>(null);
  const [fileInfo, setFileInfo] = useState<FileInfo[]>([]);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    onChange(fileInfo.map(info => info.file));
  }, [fileInfo, onChange]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      const newFileInfo = Array.from(selectedFiles).map(file => ({ id: v4().substring(8), file }));
      setFileInfo(ff => ff.concat(newFileInfo));
    }
  };

  const handleDirectoryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      let filesArray = Array.from(selectedFiles);
      if (filesArray.some(f => f.webkitRelativePath.includes('/'))) {
        const targetDirectories = new Set(filesArray.map(f => f.webkitRelativePath.split('/')[0]));
        if (targetDirectories.size === 1) {
          const folder = [...targetDirectories][0];
          filesArray = filesArray.map(f => new File([f], f.webkitRelativePath.replace(folder, '')));
          onFolderChange?.(folder);
        }
      }
      const newFileInfo = filesArray.map(file => ({ id: v4().substring(8), file: new File([file], file.webkitRelativePath || file.name) }));
      setFileInfo(ff => ff.concat(newFileInfo));
    }
  };

  const handleRemoveFile = useCallback((index: number) => (evt: React.MouseEvent) => {
    evt.preventDefault();
    evt.stopPropagation();
    setFileInfo(prevFiles => prevFiles.filter((_, i) => i !== index));
  }, []);

  const handleClearAll = useCallback((evt: React.MouseEvent) => {
    evt.preventDefault();
    evt.stopPropagation();
    setFileInfo([]);
  }, []);

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFiles = event.dataTransfer.items;

    for (const item of droppedFiles) {
      const entry = item.webkitGetAsEntry();
      if (entry) {
        let newFiles = await traverseDirectory(entry);
        if (newFiles.some(f => f.file.name.includes('/'))) {
          const targetDirectories = new Set(newFiles.map(f => f.file.name.split('/')[0]));
          if (targetDirectories.size === 1) {
            const folder = [...targetDirectories][0];
            newFiles = newFiles.map(f => ({ ...f, file: new File([f.file], f.file.name.replace(folder, '')) }));
            onFolderChange?.(folder);
          }
        }
        setFileInfo(ff => ff.concat(...newFiles));
      }
    }
    setIsActive(false);
  };

  const traverseDirectory = async (item: FileSystemEntry | FileSystemDirectoryEntry, path = ""): Promise<FileInfo[]> => {
    if (item.isFile) {
      const file = await new Promise<File>((resolve, reject) => (item as FileSystemFileEntry).file(resolve, reject));
      return [{ id: v4().substring(6), file: new File([file], `${path ? `${path}/` : ''}${file.name}`) }];
    } else { // item.isDirectory
      const dirReader = (item as FileSystemDirectoryEntry).createReader();
      const entries = await new Promise<FileSystemEntry[]>((resolve, reject) => dirReader.readEntries(resolve, reject));
      const updatedFiles: FileInfo[] = [];
      for (const entry of entries) {
        const newFiles = await traverseDirectory(entry, `${path ? `${path}/` : ''}${item.name}`);
        updatedFiles.push(...newFiles);
      }
      return updatedFiles;
    }
  }

  const openExplorerDialog = useCallback((directoryMode?: boolean) => () => {
    directoryMode ? directoryBrowser.current?.click() : fileBrowser.current?.click();
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsActive(false);
  }, []);

  return (
    <>
      <input
        type="file"
        hidden
        id="file-browser"
        onChange={handleFileChange}
        multiple
        ref={fileBrowser}
      />
      <input
        type="file"
        hidden
        id="directory-browser"
        onChange={handleDirectoryChange}
        multiple
        directory=""
        webkitdirectory=""
        ref={directoryBrowser}
      />
      <div className="drag-drop">
        <div
          className={classNames('document-uploader', { 'active': isActive })}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <Flex className="mb-3">
            <IconButton className={classNames('upload-button me-4', { 'shrinked': fileInfo.length })} onClick={openExplorerDialog(true)}>
              <FontAwesomeIcon icon={solid('folder-plus')} fixedWidth size="xl" />
              <span className={classNames(fileInfo.length ? 'ms-2' : 'mt-2')}>Upload Folders</span>
            </IconButton>
            <IconButton className={classNames('upload-button', { 'shrinked': fileInfo.length })} onClick={openExplorerDialog()}>
              <FontAwesomeIcon icon={solid('file-upload')} fixedWidth size="xl" />
              <span className={classNames(fileInfo.length ? 'ms-2' : 'mt-2')}>Upload Files</span>
            </IconButton>
          </Flex>
          <div className="upload-info mb-2">
            <FontAwesomeIcon size="lg" icon={solid('upload')} />
            <h6 className="mb-0 ms-3">{title}</h6>
          </div>
          <p className="text-center mb-2">{subtitle}</p>

          {fileInfo.length > 0 && (
            <>
              <div className="d-flex align-items-center justify-content-between w-100 mb-2">
                <Col>
                  <small className="text-muted">{fileInfo.length} file{fileInfo.length > 0 && 's'} selected</small>
                </Col>
                <Button variant="danger" size="sm" onClick={handleClearAll}>
                  <FontAwesomeIcon icon={solid('trash')} />
                  <span className="ms-2">Clear all</span>
                </Button>
              </div>
              <div className="file-list">
                <div className="file-list-container">
                  {fileInfo.map((info, index) => (
                    <div className="file-item" key={info.id}>
                      <div className="file-info">{info.file.name}</div>
                      <div className="file-actions">
                        <FontAwesomeIcon onClick={handleRemoveFile(index)} icon={solid('times')} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}